import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { 
  FiTrendingUp, FiBook, FiBarChart2, 
  FiCpu, FiSettings, FiBell
} = FiIcons

const Header = () => {
  const { user, profile } = useAuth()
  const location = useLocation()

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiTrendingUp },
    { path: '/journal', label: 'Journal', icon: FiBook },
    { path: '/analytics', label: 'Analytics', icon: FiBarChart2 },
    { path: '/insights', label: 'AI Insights', icon: FiCpu },
    { path: '/settings', label: 'Settings', icon: FiSettings }
  ]

  return (
    <motion.header 
      className="bg-white/80 dark:bg-lux-dark-bg/80 backdrop-blur-xl sticky top-0 z-40 border-b border-lux-light-border dark:border-lux-dark-border"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Brand Logo */}
          <Link to="/dashboard" className="flex items-center space-x-3 group relative z-50">
            <div className="bg-primary-gradient p-2.5 rounded-xl shadow-lg shadow-lux-primary/20 group-hover:scale-105 transition-all duration-500">
              <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
              Trading<span className="text-lux-primary">AI</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link 
                  key={item.path} 
                  to={item.path} 
                  className={`relative flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActive 
                      ? 'text-lux-primary' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {isActive && (
                    <motion.div 
                      layoutId="nav-active"
                      className="absolute inset-0 bg-lux-primary/10 rounded-xl"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <SafeIcon icon={item.icon} className="h-4 w-4 relative z-10" />
                  <span className="relative z-10">{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* Right Actions (Profile) */}
          <div className="flex items-center space-x-4">
            {/* Notification Bell (Mobile & Desktop) */}
            <button className="p-2.5 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
              <SafeIcon icon={FiBell} className="h-5 w-5" />
            </button>

            {/* Profile Avatar */}
            <div className="flex items-center space-x-3">
              <div className="hidden md:block text-right">
                <p className="text-xs font-bold text-gray-900 dark:text-white leading-none">
                  {profile?.display_name || 'Trader'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 border border-white/10 flex items-center justify-center text-white font-bold shadow-lg">
                {profile?.display_name?.[0] || user?.email?.[0].toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  )
}

export default Header